import {
  LogMiddlewareServiceAttributes,
  LogMiddlewareServiceDetailAttributes,
} from '@albatrosdeveloper/ave-models-package';
import { ColDef, GridConfig } from 'app/shared/components/smart-grid/public-api';
import { DataStatusLatencyComponent } from '../data-status-latency/data-status-latency.component';
import { DataSentLatencyComponent } from '../data-sent-latency/data-sent-latency.component';
import { DataReceivedLatencyComponent } from '../data-received-latency/data-received-latency.component';
import {
  IntegrationErrorActionsCellComponent,
  IntegrationErrorCompanyCellComponent,
  IntegrationErrorInterfaceCellComponent,
  IntegrationErrorResultCellComponent,
  IntegrationErrorTypeCellComponent,
} from './cell-renderers/integration-error-cells';

/** Formato de fecha idéntico al de la pantalla de Latencia: dd-MM-yyyy HH:mm:ss. */
export const formatLatencyDate = (value: unknown): string => {
  if (!value) return '--:--:--';
  const d = new Date(value as string | Date);
  if (isNaN(d.getTime())) return '--:--:--';
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  const hours = d.getHours().toString().padStart(2, '0');
  const minutes = d.getMinutes().toString().padStart(2, '0');
  const seconds = d.getSeconds().toString().padStart(2, '0');
  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

/** Duración en HH:MM:SS(.ms), misma lógica que `formatMiliSeconds` de Latencia. */
export const formatLatencyDuration = (ms: number | undefined | null): string => {
  if (ms === undefined || ms === null || ms < 0) return '--:--:--';
  const hours = Math.floor(ms / (1000 * 60 * 60)).toString().padStart(2, '0');
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60)).toString().padStart(2, '0');
  const seconds = Math.floor((ms % (1000 * 60)) / 1000).toString().padStart(2, '0');
  const milliseconds = ms % 1000;
  if (Number(seconds) === 0) {
    return `${hours}:${minutes}:${seconds}.${milliseconds}`;
  }
  return `${hours}:${minutes}:${seconds}`;
};

export interface IntegrationErrorsColumnCallbacks {
  /** Abre el modal de respuesta (procesados/insertados/actualizados/errores). */
  onOpenResult: (row: LogMiddlewareServiceAttributes) => void;
  /** Abre el detalle de logs del registro. */
  onViewLogs: (row: LogMiddlewareServiceAttributes) => void;
}

/**
 * Columnas del modal de errores de integración SAP: mismo orden y renderizado
 * que la pantalla de Latencia (Interfaz con código+nombre, fechas dd-MM-yyyy
 * HH:mm:ss, resultado como botón de respuesta, icono de estado, duración
 * HH:MM:SS y menú de acciones) más la columna de Compañía. El detalle se abre
 * desde el menú de acciones — no con clic en la fila.
 */
export const buildIntegrationErrorsColumnDefs = (
  callbacks: IntegrationErrorsColumnCallbacks
): ColDef<LogMiddlewareServiceAttributes>[] => [
  {
    field: 'code',
    headerName: 'Interfaz',
    minWidth: 190,
    flex: 1,
    sortable: true,
    filterable: true,
    cellRenderer: IntegrationErrorInterfaceCellComponent,
  },
  {
    field: 'type',
    headerName: 'Tipo',
    width: 210,
    sortable: true,
    filterable: true,
    cellRenderer: IntegrationErrorTypeCellComponent,
  },
  {
    // Dot-path para que el filtro de columna opere sobre el NOMBRE (con
    // `field: 'company'` el filtro enumeraba objetos → "[object Object]").
    field: 'company.name' as any,
    headerName: 'Compañía',
    width: 170,
    sortable: false,
    filterable: true,
    cellRenderer: IntegrationErrorCompanyCellComponent,
  },
  {
    field: 'dateStart',
    headerName: 'Inicio Ejecución',
    width: 165,
    sortable: true,
    filterable: false,
    valueFormatter: formatLatencyDate,
  },
  {
    field: 'dateEnd',
    headerName: 'Fin Ejecución',
    width: 165,
    sortable: true,
    filterable: false,
    valueFormatter: formatLatencyDate,
  },
  {
    // Header explícito: con headerName vacío el smart-grid "humaniza" el
    // nombre del field y muestra "Result"/"Status" en inglés.
    field: 'result',
    headerName: 'Respuesta',
    width: 90,
    sortable: false,
    filterable: false,
    cellRenderer: IntegrationErrorResultCellComponent,
    cellRendererParams: { onClick: callbacks.onOpenResult },
  },
  {
    field: 'status',
    headerName: 'Estado',
    width: 80,
    sortable: false,
    filterable: false,
    cellRenderer: DataStatusLatencyComponent,
  },
  {
    field: 'duration',
    headerName: 'Duración',
    width: 120,
    sortable: true,
    filterable: false,
    valueFormatter: formatLatencyDuration,
  },
  {
    field: 'actions' as keyof LogMiddlewareServiceAttributes,
    headerName: 'Acción',
    width: 90,
    sortable: false,
    filterable: false,
    cellRenderer: IntegrationErrorActionsCellComponent,
    cellRendererParams: { onClick: callbacks.onViewLogs },
  },
];

export const INTEGRATION_ERRORS_GRID_CONFIG: GridConfig<LogMiddlewareServiceAttributes> = {
  dataMode: 'api',
  rowIdField: '_id',
  pagination: {
    enabled: true,
    mode: 'api',
    pageSize: 25,
    pageSizeOptions: [25, 50, 100],
    showPaginator: true,
  },
  sorting: { enabled: true, mode: 'api', multiColumn: false },
  search: { enabled: false },
  columnFilters: { enabled: true, mode: 'api' },
  selection: { enabled: false },
  columnVisibility: { enabled: false },
  emptyState: {
    message: 'No hay errores de integración registrados.',
    showIcon: true,
  },
  // height 100%: la grilla llena el contenido del modal y el paginador
  // (mt-auto en .smart-grid-pagination) queda siempre al pie, sin espacio
  // muerto debajo cuando hay pocos registros.
  styling: { fontSize: '12px', height: '100%' },
};

export interface IntegrationErrorDetailColumnCallbacks {
  /** Abre el visor de JSON con la data enviada o recibida del renglón. */
  onOpenJson: (title: string, json: unknown) => void;
}

/**
 * Columnas del detalle por registro: mismas que el modal de logs de Latencia —
 * la data enviada/recibida son botones de burbuja que abren el visor de JSON.
 */
export const buildIntegrationErrorDetailColumnDefs = (
  callbacks: IntegrationErrorDetailColumnCallbacks
): ColDef<LogMiddlewareServiceDetailAttributes>[] => [
  {
    field: 'documentIdentifier',
    headerName: 'Identificador Documento',
    minWidth: 170,
    sortable: true,
  },
  {
    field: 'message',
    headerName: 'Mensaje',
    minWidth: 240,
    flex: 1,
    sortable: true,
  },
  {
    field: 'dataSent',
    headerName: 'Data Enviada',
    width: 150,
    sortable: false,
    filterable: false,
    cellRenderer: DataSentLatencyComponent,
    cellRendererParams: {
      onClick: (json: unknown) => callbacks.onOpenJson('Data enviada', json),
    },
  },
  {
    field: 'dataReceived',
    headerName: 'Data Recibida',
    width: 150,
    sortable: false,
    filterable: false,
    cellRenderer: DataReceivedLatencyComponent,
    cellRendererParams: {
      onClick: (json: unknown) => callbacks.onOpenJson('Data recibida', json),
    },
  },
  {
    field: 'status',
    headerName: 'Estado',
    width: 110,
    sortable: false,
    filterable: false,
    cellRenderer: DataStatusLatencyComponent,
  },
];

export const INTEGRATION_ERROR_DETAIL_GRID_CONFIG: GridConfig<LogMiddlewareServiceDetailAttributes> = {
  dataMode: 'local',
  rowIdField: '_id',
  pagination: {
    enabled: true,
    mode: 'local',
    pageSize: 50,
    pageSizeOptions: [50, 100, 150, 200],
    showPaginator: true,
  },
  sorting: { enabled: true, mode: 'local', multiColumn: false },
  search: { enabled: true, mode: 'local' },
  columnFilters: { enabled: true, mode: 'local' },
  selection: { enabled: false },
  columnVisibility: { enabled: false },
  emptyState: { message: 'Sin detalle para este registro.', showIcon: true },
  styling: { fontSize: '12px', height: '100%' },
};
